/**
 * Every English string of the kitchen-flow item-master screens (Lane A):
 * scanning, barcodes, purchase units, lots and bulk import. Kept in one place
 * so an Urdu translation can be dropped in later without hunting through JSX.
 *
 * Plain words on purpose — the people at the store shelf are not office
 * staff.
 */
export const IM = {
  scan: {
    button: 'Scan',
    title: 'Scan a barcode',
    cameraHint: 'Hold the barcode flat inside the box.',
    typeLabel: 'Or type the number under the barcode',
    find: 'Find',
    looking: 'Looking…',
    notFoundTitle: 'New barcode',
    notFound: (code: string) => `No item has barcode ${code} yet.`,
    createItem: 'Create item with this barcode',
    archived: (name: string) => `"${name}" has this barcode but is archived.`,
    wedgeTip: 'A USB scanner works anywhere on this page — just scan.',
    badRead: 'That did not scan cleanly — try again or type the number.',
    listTip: 'Scan each pack as you check it.',
    added: (name: string, qty: string, unit: string) => `${name}: ${qty} ${unit} so far`,
    notOnList: (name: string, what: string) => `${name} is not on this ${what}.`,
    wrongItem: (scanned: string, expected: string) => `That is ${scanned}, not ${expected}.`,
  },
  barcodes: {
    heading: 'Barcodes',
    none: 'No barcode yet. Scan or type one to add it.',
    add: 'Add barcode',
    codeLabel: 'Barcode',
    packLabel: 'How many in this pack',
    packNameLabel: 'Pack name (optional)',
    packNamePlaceholder: 'Carton, Tray…',
    perScan: (qty: string, unit: string) => `${qty} ${unit} per scan`,
    remove: 'Remove',
    added: 'Barcode added',
    removed: 'Barcode removed',
    prefillFound: (name: string) => `Found online: ${name}. Check the details before saving.`,
  },
  returnable: {
    label: 'Comes back after events',
    help: 'Tick for equipment that goes out and must be returned — chafing dishes, crockery, trays.',
  },
  purchaseUnit: {
    heading: 'How you buy it',
    labelLabel: 'Buying unit',
    labelPlaceholder: 'Bag 25 kg',
    factorLabel: (unit: string) => `How many ${unit} in one`,
    help: 'Leave empty if you buy it the same way you count it.',
    shown: (label: string, factor: string, unit: string) => `Bought as ${label} (1 = ${factor} ${unit})`,
    enterIn: (label: string) => `Enter in ${label}`,
    equals: (qty: string, unit: string) => `= ${qty} ${unit}`,
  },
  lots: {
    heading: 'Batches on the shelf',
    none: 'No open batches.',
    useFirst: (lot: string, days: number | null | undefined) =>
      days == null
        ? `Use batch ${lot} first`
        : days < 0
          ? `Use batch ${lot} first — expired ${-days} day${days === -1 ? '' : 's'} ago`
          : days === 0
            ? `Use batch ${lot} first — expires today`
            : `Use batch ${lot} first — expires in ${days} day${days === 1 ? '' : 's'}`,
    expiredThrowAway: (qty: string, unit: string, from: string, to: string) =>
      from === to ? `${qty} ${unit} expired on ${from} — throw it away` : `${qty} ${unit} expired between ${from} and ${to} — throw it away`,
    receivedOn: (date: string) => `received ${date}`,
    lotNoLabel: 'Batch / lot no.',
    lotNoPlaceholder: 'On the pack (optional)',
    expiryLabel: 'Expiry date (optional)',
    expiryRequiredLabel: 'Expiry date',
    expiryNeeded: 'This item goes off, so the date is needed.',
    noExpiry: 'no expiry',
  },
  photos: {
    heading: 'Photo of the delivery slip',
    take: 'Take photo',
    none: 'No photo yet. Photograph the slip the driver hands over.',
    added: 'Photo saved',
    removed: 'Photo removed',
    remove: 'Remove',
    open: 'Open photo',
    alt: 'Delivery slip',
  },
  import: {
    button: 'Import items',
    title: 'Import items from Excel',
    step1: '1. Download the template and fill it in (one item per row).',
    download: 'Download template',
    step2: '2. Choose your filled file (.xlsx or .csv).',
    choose: 'Choose file',
    checking: 'Checking your file…',
    summary: (ok: number, bad: number) => `${ok} ready · ${bad} need fixing`,
    fixFirst: 'Fix the red rows in your file, then choose it again. Nothing is saved until every row is OK.',
    newCategories: (names: string[]) => `New categories will be created: ${names.join(', ')}`,
    confirm: (n: number) => `Import ${n} item${n === 1 ? '' : 's'}`,
    importing: 'Importing…',
    done: (n: number) => `${n} item${n === 1 ? '' : 's'} imported`,
    tooBig: 'The file is too big (5 MB max).',
    colRow: 'Row',
    colName: 'Name',
    colUnit: 'Unit',
    colOpening: 'Opening qty',
    colExpiry: 'Expiry',
    colCost: 'Cost',
    colBarcodes: 'Barcodes',
    colProblem: 'Problem',
    ok: 'OK',
  },
} as const

/** Label for a lot in hints: its lot number, else the date it came in. */
export function lotName(lot: { lot_no?: string | null; received_at: string }): string {
  if (lot.lot_no) return lot.lot_no
  return `from ${lot.received_at.slice(0, 10)}`
}
